import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Imported from the domain modules rather than the `adminData` barrel: that
// index file is the one frontend file two other in-flight slices have both
// edited, and this page needs nothing from it that its own modules do not
// already export.
import {
  fetchAdminTenanciesPage,
  fetchAdminTenancyStatusSummary,
} from "../../api/adminData/tenancies";
import {
  fetchAdminTenantsPage,
  deleteAdminTenant,
} from "../../api/adminData/tenants";
import { fetchAdminRooms, normalizeRoom } from "../../api/adminData/rooms";
import { fetchAdminUnitsPage } from "../../api/adminData/units";
import { getUnitHeadingLabel } from "../../utils/unitDisplayId";

import TenantCreateModal from "../../components/admin/tenants/TenantCreateModal";
import TenantOverviewStats from "./tenants/TenantOverviewStats";
import TenantOverviewToolbar from "./tenants/TenantOverviewToolbar";
import TenantUnitGroup from "./tenants/TenantUnitGroup";
import UnassignedTenantsSection from "./tenants/UnassignedTenantsSection";
import useUnassignedTenants from "./hooks/useUnassignedTenants";
import {
  buildTenancyRow,
  groupRowsByUnit,
} from "./tenants/tenantOverviewData";

/**
 * Mieterübersicht — tenancies grouped by unit.
 *
 * The page is an orchestrator: it owns the query state, issues the requests
 * and hands plain data to the presentation components under `./tenants/`.
 *
 * Two decisions drive everything else here.
 *
 * **The row is a tenancy, not a tenant.** Grouping by unit only makes sense
 * over occupancy contracts, a tenant with two tenancies used to be collapsed
 * into whichever one the page picked as "current", and — decisively — the
 * tenancies endpoint filters, searches, sorts and pages server-side while the
 * tenants endpoint offers only `q`/`skip`/`limit`.
 *
 * **KPI totals never come from the rows.** Each card queries its own source
 * with `limit: 1` and reads `total`, so what the cards report is independent
 * of which page of results is on screen. The previous version summed the
 * loaded array and was therefore capped at whatever had been fetched.
 *
 * `include_cancelled` is always sent. Without it the endpoint hides cancelled
 * tenancies from any unfiltered list, which is how the same tenancy could
 * read "Inaktiv" here and "Ausgezogen" on the detail page while the card
 * beside it said "Storniert".
 *
 * **A second source, for the people the first one cannot describe.** A tenant
 * who holds no tenancy has no row in `/api/admin/tenancies` — that endpoint
 * starts from `select(Tenancy)` — so the page used to count them in "Mieter
 * gesamt" and then be unable to show them, right down to rendering "Keine
 * Mietverhältnisse erfasst." above a card reading 1. `useUnassignedTenants`
 * asks `/api/admin/tenants?without_tenancy=true` for exactly those people and
 * `UnassignedTenantsSection` renders them in their own block with their own
 * columns. The two sets never merge and never share a pager: they are ordered
 * by different columns server-side, so no single offset could walk both.
 */

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 25;
/** Endpoint maximum; the filter's option list is capped by it, not the data. */
const UNIT_OPTION_LIMIT = 200;

const PENDING = { loading: true, error: null, value: null };

function kpiOk(value) {
  return { loading: false, error: null, value };
}

function kpiFailed(error) {
  return { loading: false, error: error || true, value: null };
}

function AdminTenantsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [unitId, setUnitId] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  const [rows, setRows] = useState({
    loading: true,
    error: null,
    items: [],
    total: 0,
  });
  const [roomsById, setRoomsById] = useState(() => new Map());
  const [unitOptions, setUnitOptions] = useState([]);
  const [collapsedUnits, setCollapsedUnits] = useState(() => new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [tenantsTotal, setTenantsTotal] = useState(PENDING);
  // One request feeds both tenancy cards: the summary carries every derived
  // status, so "Aktiv" and "Storniert" can never drift apart.
  const [statusSummary, setStatusSummary] = useState(PENDING);

  // Guards a late response from a superseded query overwriting a newer one.
  const requestSeq = useRef(0);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Any change to what is being asked for starts again at the first page.
  useEffect(() => {
    setPage(0);
  }, [search, status, unitId, pageSize]);

  const loadRows = useCallback(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setRows((prev) => ({ ...prev, loading: true, error: null }));

    return fetchAdminTenanciesPage({
      search: search || undefined,
      status: status || undefined,
      unit_id: unitId || undefined,
      include_cancelled: true,
      limit: pageSize,
      skip: page * pageSize,
    })
      .then((res) => {
        if (requestSeq.current !== seq) return;
        setRows({
          loading: false,
          error: null,
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total || 0),
        });
      })
      .catch((e) => {
        if (requestSeq.current !== seq) return;
        setRows({
          loading: false,
          error: e?.message || "Mietverhältnisse konnten nicht geladen werden.",
          items: [],
          total: 0,
        });
      });
  }, [search, status, unitId, page, pageSize]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const loadKpis = useCallback(() => {
    setTenantsTotal(PENDING);
    setStatusSummary(PENDING);

    // `limit: 1` — only `total` is read. Each card fails on its own.
    fetchAdminTenantsPage({ limit: 1 })
      .then((r) => setTenantsTotal(kpiOk(Number(r?.total || 0))))
      .catch((e) => setTenantsTotal(kpiFailed(e)));

    // Counts by DERIVED display status, the same value the rows render as
    // their badge. The list endpoint's `status` filter answers a different
    // question — it matches the stored column, which reports a stored
    // `reserved` whose move-in has passed as reserved while the row beside it
    // reads "Aktiv". Counting through it understated this card.
    fetchAdminTenancyStatusSummary()
      .then((s) => setStatusSummary(kpiOk(s)))
      .catch((e) => setStatusSummary(kpiFailed(e)));
  }, []);

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  // Rooms carry the area. `GET /api/admin/rooms` takes no skip/limit and
  // returns the organisation's rooms in full, so unlike the units endpoint it
  // can be joined against without silently losing rows past a page boundary.
  useEffect(() => {
    let cancelled = false;
    fetchAdminRooms()
      .then((list) => {
        if (cancelled) return;
        const map = new Map();
        for (const raw of Array.isArray(list) ? list : []) {
          const room = normalizeRoom(raw);
          if (room?.id != null) map.set(String(room.id), room);
        }
        setRoomsById(map);
      })
      .catch(() => {
        // The area is decoration; the row renders "—" without it and the page
        // must not fail over a missing square-metre figure.
        if (!cancelled) setRoomsById(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Options for the unit filter only. The filter itself is applied by the
  // server via `unit_id`, so a truncated option list can hide a choice but
  // never mis-filter the rows it returns.
  //
  // The label is the persisted short code — CL-001, APT-002 — through the same
  // `getUnitHeadingLabel` the unit tables and pickers already use. It used to
  // prefer `u.title`, which is free text: an organisation that titles its units
  // by location got a dropdown reading "Zürich" seven times over, naming no
  // unit at all. `short_unit_id` is NOT NULL and unique per organisation
  // (migration 068), so it identifies every row and needs no tie-breaker.
  //
  // Only the visible text changed. The option value stays `unit.id`, which is
  // what `GET /api/admin/tenancies?unit_id=` matches.
  useEffect(() => {
    let cancelled = false;
    fetchAdminUnitsPage({ limit: UNIT_OPTION_LIMIT })
      .then((res) => {
        if (cancelled) return;
        const opts = (res?.items || [])
          .map((u) => ({
            id: String(u.id),
            label: getUnitHeadingLabel(u),
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "de-CH"));
        setUnitOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setUnitOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The "Ohne Mietverhältnis" block. Its own request, its own paging, its own
  // error — nothing here can move the tenancy list's page.
  //
  // A tenancy STATUS and a UNIT are properties of a contract. For someone who
  // holds none they are not false, they are undefined, so the block is not
  // filtered by them — with either set it is absent and asks the server
  // nothing. The text search does apply: a name is a fact about the person.
  const unassignedEnabled = !status && !unitId;
  const unassigned = useUnassignedTenants({
    search,
    enabled: unassignedEnabled,
  });
  const reloadUnassigned = unassigned.reload;

  // The tenant detail page signals a return trip through router state.
  //
  // The unassigned block is refreshed here too, and it is the case that makes
  // the block correct rather than merely present: giving somebody their first
  // tenancy is done on the detail page, and on the way back they must leave
  // this block. Without the reload they would sit here as a person with no
  // contract until the next full page load.
  useEffect(() => {
    if (!location.state?.refreshTenants) return;
    loadRows();
    loadKpis();
    reloadUnassigned();
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    location.state?.refreshTenants,
    location.pathname,
    navigate,
    loadRows,
    loadKpis,
    reloadUnassigned,
  ]);

  const displayRows = useMemo(
    () => rows.items.map((t) => buildTenancyRow(t, roomsById)),
    [rows.items, roomsById]
  );

  const groups = useMemo(() => groupRowsByUnit(displayRows), [displayRows]);

  const totalPages = Math.max(1, Math.ceil((rows.total || 0) / pageSize));
  const paginated = totalPages > 1;
  const hasFilters = Boolean(search || status || unitId);

  const handleOpenTenant = useCallback(
    (row) => {
      if (!row?.tenantId) return;
      navigate(`/admin/tenants/${encodeURIComponent(row.tenantId)}`);
    },
    [navigate]
  );

  /**
   * Open ONE named person, which is not always the row's tenant.
   *
   * A row is a tenancy and `row.tenantId` is its primary tenant, so the row
   * handler above answers "open this contract's holder". A co-tenant is a
   * tenant too, and clicking their name has to open them rather than the person
   * they live with.
   */
  const handleOpenPerson = useCallback(
    (tenantId) => {
      if (!tenantId) return;
      navigate(`/admin/tenants/${encodeURIComponent(String(tenantId))}`);
    },
    [navigate]
  );

  // One implementation for both blocks: the id is the only thing that differs,
  // and a delete changes what BOTH lists should show — the tenancy rows, the
  // KPI count and the unassigned block are all refreshed from the server.
  // (For a tenancy row the backend still refuses — `delete_tenant` blocks while
  // any tenancy exists — and the refusal is surfaced unchanged. For a row in
  // the unassigned block it is the one case where the action can succeed.)
  const deleteTenantById = useCallback(
    (tenantId) => {
      if (!tenantId) return;
      if (!window.confirm("Diesen Mieter wirklich unwiderruflich löschen?")) {
        return;
      }
      setDeleteError(null);
      deleteAdminTenant(tenantId)
        .then(() => {
          loadRows();
          loadKpis();
          reloadUnassigned();
        })
        .catch((err) => {
          setDeleteError(
            (err && typeof err.message === "string" && err.message) ||
              String(err || "") ||
              "Löschen fehlgeschlagen."
          );
        });
    },
    [loadRows, loadKpis, reloadUnassigned]
  );

  const handleDeleteTenant = useCallback(
    (row) => deleteTenantById(row?.tenantId),
    [deleteTenantById]
  );

  const buildActions = useCallback(
    (row) => [
      row.tenantId && {
        key: "open-tenant",
        label: "Mieter öffnen",
        onSelect: () => handleOpenTenant(row),
      },
      row.unitId && {
        key: "open-unit",
        label: "Unit öffnen",
        onSelect: () =>
          navigate(`/admin/units/${encodeURIComponent(row.unitId)}`),
      },
      row.tenantId && {
        key: "delete-tenant",
        label: "Mieter löschen",
        destructive: true,
        onSelect: () => handleDeleteTenant(row),
      },
    ],
    [handleOpenTenant, handleDeleteTenant, navigate]
  );

  // The unassigned block's row is a TENANT, so it carries `id`, not `tenantId`.
  // Its menu holds two of the three tenancy-row actions: there is no unit to
  // open, and "Mieter öffnen" plus "Mieter löschen" are exactly the two that
  // mean something for a person without a contract.
  const handleOpenUnassignedTenant = useCallback(
    (tenant) => {
      if (!tenant?.id) return;
      navigate(`/admin/tenants/${encodeURIComponent(String(tenant.id))}`);
    },
    [navigate]
  );

  const buildUnassignedActions = useCallback(
    (tenant) => [
      tenant?.id && {
        key: "open-tenant",
        label: "Mieter öffnen",
        onSelect: () => handleOpenUnassignedTenant(tenant),
      },
      tenant?.id && {
        key: "delete-tenant",
        label: "Mieter löschen",
        destructive: true,
        onSelect: () => deleteTenantById(String(tenant.id)),
      },
    ],
    [handleOpenUnassignedTenant, deleteTenantById]
  );

  const setUnassignedSkip = unassigned.setSkip;
  const unassignedPageSize = unassigned.pageSize;
  const handleUnassignedPageChange = useCallback(
    (nextPage) => {
      setUnassignedSkip(Math.max(0, nextPage) * unassignedPageSize);
    },
    [setUnassignedSkip, unassignedPageSize]
  );

  const toggleUnit = useCallback((key) => {
    setCollapsedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setSearchInput("");
    setSearch("");
    setStatus("");
    setUnitId("");
  }, []);

  return (
    <div className="-m-6 min-h-screen bg-background text-foreground">
      <TenantCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          // A freshly created tenant holds no tenancy, so `loadRows` — which
          // reads the tenancies endpoint — can never show it. Refreshing the
          // unassigned block is what makes "Anlegen" visibly do something
          // beyond the KPI card ticking up. Reloaded from the server rather
          // than pushed in optimistically: the server decides whether the new
          // person matches the active search.
          loadRows();
          loadKpis();
          reloadUnassigned();
        }}
      />

      <div className="mx-auto flex max-w-[1500px] flex-col gap-4 px-6 py-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-[40px] w-[40px] items-center justify-center rounded-[9px] border border-sky-500/25 bg-sky-500/10 text-[18px]"
            >
              🏢
            </span>
            <div>
              <h1 className="m-0 text-[24px] font-semibold leading-tight tracking-[-0.01em] text-foreground">
                Mieterübersicht
              </h1>
              <p className="m-0 text-[12.5px] text-muted-foreground">
                Nach Unit gruppiert
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="h-[34px] rounded-[7px] bg-primary px-[14px] text-[13px] font-medium text-primary-foreground hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            + Mieter hinzufügen
          </button>
        </header>

        {deleteError ? (
          <div
            role="alert"
            className="rounded-[8px] border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-700 dark:text-rose-300"
          >
            {deleteError}
          </div>
        ) : null}

        <TenantOverviewStats
          tenantsTotal={tenantsTotal}
          statusSummary={statusSummary}
        />

        <TenantOverviewToolbar
          search={searchInput}
          onSearchChange={setSearchInput}
          status={status}
          onStatusChange={setStatus}
          unitId={unitId}
          onUnitChange={setUnitId}
          unitOptions={unitOptions}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />

        <UnassignedTenantsSection
          loading={unassigned.loading}
          error={unassigned.error}
          items={unassigned.items}
          total={unassigned.total}
          skip={unassigned.skip}
          pageSize={unassigned.pageSize}
          onPageChange={handleUnassignedPageChange}
          onOpenTenant={handleOpenUnassignedTenant}
          buildActions={buildUnassignedActions}
        />

        {rows.error ? (
          <div
            role="alert"
            className="rounded-[10px] border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-700 dark:text-rose-300"
          >
            <strong className="font-semibold">Fehler beim Laden:</strong>{" "}
            {rows.error}
          </div>
        ) : rows.loading ? (
          <div
            className="flex flex-col gap-3"
            aria-busy="true"
            data-testid="tenant-overview-loading"
          >
            <span className="sr-only">Mietverhältnisse werden geladen …</span>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                aria-hidden="true"
                className="h-[140px] animate-pulse rounded-[10px] border border-border bg-muted/40"
              />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-[10px] border border-border bg-card px-5 py-8 text-center">
            <p className="m-0 text-[14px] font-medium text-foreground">
              {hasFilters
                ? "Keine Mietverhältnisse entsprechen den aktuellen Filtern."
                : "Keine zugeordneten Mietverhältnisse vorhanden."}
            </p>
            {hasFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-3 rounded-[7px] border border-border bg-card px-[14px] py-[6px] text-[13px] text-foreground hover:bg-muted"
              >
                Filter zurücksetzen
              </button>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {groups.map((group) => {
              const key = group.unitId || "__none__";
              return (
                <TenantUnitGroup
                  key={key}
                  group={group}
                  collapsed={collapsedUnits.has(key)}
                  onToggle={() => toggleUnit(key)}
                  paginated={paginated}
                  onOpenTenant={handleOpenTenant}
                  onOpenPerson={handleOpenPerson}
                  buildActions={buildActions}
                />
              );
            })}
          </div>
        )}

        {!rows.loading && !rows.error && rows.total > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-muted-foreground">
            <span>
              {rows.total} {rows.total === 1 ? "Mietverhältnis" : "Mietverhältnisse"}
              {paginated ? ` · Seite ${page + 1} von ${totalPages}` : ""}
            </span>
            {paginated ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-[7px] border border-border bg-card px-[12px] py-[5px] text-foreground disabled:opacity-40 hover:bg-muted"
                >
                  Zurück
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={page >= totalPages - 1}
                  className="rounded-[7px] border border-border bg-card px-[12px] py-[5px] text-foreground disabled:opacity-40 hover:bg-muted"
                >
                  Weiter
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default AdminTenantsPage;
